import {
  Box,
  Container,
  Flex,
  Grid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import { Marker, Union } from '@icon'
import { useStore } from '@store'
import { EventCard, Link, MotionBox } from '@ui'
import { observer } from 'mobx-react-lite'
import useTranslation from 'next-translate/useTranslation'
import { fadeIn } from '@lib/motion'
import { useInViewMotion } from '@lib/hooks'
import { createNavLink, getOpeningHourLabelCurrentMuseum } from '@utils'
import { useRouter } from 'next/router'
import { ITabData } from '@interfaces'
import { ContentNotFound } from '@common'
import { useRef, MutableRefObject, useMemo } from 'react'
import { getMuseumOpeningHours } from '@lib/const/museum-opening-hours.const'

interface IMuseumProgram {
  tabData: ITabData[]
}

const MuseumProgram = observer(({ tabData }: IMuseumProgram) => {
  const { ui } = useStore()
  const { t } = useTranslation()
  const { controls, ref } = useInViewMotion(true, 0)
  const { locale } = useRouter()
  const containerRef = useRef(null) as MutableRefObject<HTMLDivElement>
  const openingHours = useMemo(
    () => getMuseumOpeningHours(locale || 'tr'),
    [locale]
  )
  const openingLabel = useMemo(
    () => getOpeningHourLabelCurrentMuseum(locale || 'tr', openingHours),
    [locale, openingHours]
  )

  const scrollToTopSticky = () => {
    if (typeof window === 'undefined') return
    const yCoordinate =
      containerRef?.current?.getBoundingClientRect().top + window.pageYOffset
    const yOffset = -40
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' })
  }

  const filteredTabData = tabData.filter(
    (tab) => tab.tabLabelTx !== 'common:tabs.allTab'
  )

  return (
    <Box as="section" ref={containerRef}>
      <Container pt="6" pb={{ base: '12', lg: 'padding.primary2' }}>
        <Flex>
          {openingLabel && (
            <Text
              color="black.primary3"
              fontSize={{ base: 'xs', lg: 'sm' }}
              as="h6"
              mr={{ base: 2, lg: 5 }}
            >
              <Text fontWeight="regular" as="span">
                {openingLabel.isOpeningLabel}{' '}
              </Text>
              {openingLabel.openingHourLabel}
            </Text>
          )}
          <Link
            href="https://goo.gl/maps/L3KzMMhPLn6CKmF39"
            target="_blank"
            display="flex"
            flexDirection="row"
            alignItems="center"
            color="red.primary"
            _hover={{ color: 'red.primary3' }}
          >
            <Marker />
            <Text
              ml="6px"
              fontSize={{ base: 'xs', lg: 'sm' }}
              fontWeight="regular"
            >
              {t('common:contact.getDirections')}
            </Text>
          </Link>
        </Flex>
        <Text
          as="h2"
          mt={4}
          mb="6px"
          fontSize={{ base: 'xxl', lg: '3xl' }}
        >
          {t('common:museumProgram.title')}
        </Text>
        <Tabs isLazy lazyBehavior="unmount">
          <MotionBox
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={fadeIn.animationContainer}
          >
            <Grid
              gridAutoFlow="column"
              justifyContent="start"
              gap={{ base: 4, lg: 5 }}
              overflowX="auto"
              overflowY="hidden"
              zIndex="1"
              position="sticky"
              top={ui.scrollDir === 'down' ? '0px' : '-100%'}
              transition="all 0.3s ease"
              py="4"
              alignItems="center"
              sx={{
                '&::-webkit-scrollbar': {
                  height: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'white.primary3',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'red.primary',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'red.primary3',
                },
              }}
            >
              <Link
                href={createNavLink('whatson', locale)}
                display="flex"
                flexDirection="row"
                alignItems="center"
                color="red.primary"
                mr={{ base: 2, lg: 7 }}
                _hover={{ color: 'red.primary3' }}
                whiteSpace="pre"
              >
                <Union />
                <Text
                  ml={2}
                  fontSize={{ base: '15px', lg: '16px' }}
                  fontWeight="regular"
                >
                  {t('common:tabs.allTab')}
                </Text>
              </Link>
              <TabList
                flexDirection="row"
                borderBottom="none"
                minW="max-content"
              >
                {filteredTabData.map((tab) => (
                  <Tab
                    alignItems="left"
                    justifyContent="unset"
                    marginRight={{ base: '22px', lg: '30px' }}
                    fontSize={{ base: '15px', lg: '16px' }}
                    key={tab.id}
                    borderBottom="2px solid"
                    mb="0px"
                    px={0}
                    sx={{
                      '&[aria-selected=true]': {
                        color: 'black.primary',
                        borderColor: 'red.primary',
                        fontWeight: 'regular',
                      },
                      '&:focus': {
                        boxShadow: 'none',
                      },
                    }}
                    whiteSpace="pre"
                    onClick={scrollToTopSticky}
                  >
                    {tab.tabLabelTx === 'common:tabs.learningPrograms'
                      ? t('exhibition-and-event:home.eventTypes.learning')
                      : tab.tabLabelTx === 'common:tabs.childrenPrograms'
                      ? t('exhibition-and-event:home.eventTypes.children')
                      : t(tab.tabLabelTx)}
                  </Tab>
                ))}
              </TabList>
            </Grid>
            <TabPanels
              maxW={{ base: 'full', lg: 'container.secondary' }}
              margin={{ base: 'auto', lg: '0px auto' }}
              id="MuseumProgram"
            >
              {filteredTabData.map((tab) => {
                return tab && tab.tabContent && tab.tabContent.length < 1 ? (
                  <TabPanel key={tab.id}>
                    <ContentNotFound minHeight="50vh" />
                  </TabPanel>
                ) : (
                  <TabPanel
                    key={tab.id}
                    display="grid"
                    gridTemplateColumns={{
                      base: '1fr',
                      md: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                    }}
                    gridRowGap={{ base: 'padding.primary', lg: 9 }}
                    gridColumnGap={{ base: 0, lg: 5 }}
                    pt={{ base: '60px', lg: '62px' }}
                    px={0}
                    justifyItems="center"
                  >
                    {tab.tabContent.map((card, index) => (
                      <Box key={card.id} width="full" overflow="hidden">
                        <MotionBox
                          initial="hidden"
                          animate="visible"
                          variants={fadeIn.up}
                          transition={{ duration: 0.35, delay: index * 0.04 }}
                        >
                          <EventCard {...card} />
                        </MotionBox>
                      </Box>
                    ))}
                  </TabPanel>
                )
              })}
            </TabPanels>
          </MotionBox>
        </Tabs>
      </Container>
    </Box>
  )
})

export default MuseumProgram